import { Box, MenuItem, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  CustomButton,
  CustomSelect,
  CustomTextField,
  HorizontalForm,
  VerticalForm,
} from '../../../common/components';
import {
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_DEBT_BALANCE_REPORT_TYPE,
} from '../constants/gerencialesConstants';
import { formatCurrency } from '../../../utils/formatUtils';

/**
 * Filtros para el modulo de estadisticas gerenciales.
 * @param {Object} props - Props del componente.
 * @param {string} props.reportType - Tipo de reporte seleccionado.
 * @param {Array<{label: string, value: string}>} props.reportOptions - Opciones del selector.
 * @param {boolean} props.loading - Estado de carga.
 * @param {string|null} props.error - Mensaje de error.
 * @param {Object|null} props.header - Header de respuesta.
 * @param {string} props.dateStart - Fecha desde.
 * @param {string} props.dateEnd - Fecha hasta.
 * @param {Function} props.onDateChange - Handler de cambio de fechas.
 * @param {Function} props.onReportTypeChange - Handler de cambio de reporte.
 * @param {Function} props.onReload - Handler de recarga.
 * @param {Function} props.onDownloadPdf - Handler de descarga PDF.
 * @param {boolean} props.pdfCooldown - Bloquea descarga mientras se renderiza el grafico.
 * @param {boolean} props.disableDownload - Deshabilita descarga cuando no hay data.
 * @returns {JSX.Element} Barra de filtros.
 */
const GerencialesFilters = ({
  reportType,
  reportOptions,
  loading,
  error,
  header,
  dateStart,
  dateEnd,
  onDateChange,
  onReportTypeChange,
  onReload,
  onDownloadPdf,
  pdfCooldown,
  disableDownload,
}) => {
  const showDateFilters = GERENCIALES_DATE_RANGE_REPORTS.includes(reportType);

  return (
    <HorizontalForm>
      <CustomSelect
        label="Tipo de reporte"
        size="medium"
        value={reportType}
        onChange={onReportTypeChange}
        sx={{ maxWidth: 340 }}
      >
        {reportOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      {showDateFilters && (
        <>
          <CustomTextField
            label="Fecha Desde"
            type="date"
            name="dateStart"
            value={dateStart}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 190, ml: 2 }}
          />
          <CustomTextField
            label="Fecha Hasta"
            type="date"
            name="dateEnd"
            value={dateEnd}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 190, ml: 2 }}
          />
        </>
      )}
      <CustomButton
        variant="warning"
        onClick={onReload}
        disabled={loading}
        buttonIcon={RefreshIcon}
        sx={{ mt: 1.5, ml: 2 }}
      >
        Actualizar
      </CustomButton>
      <VerticalForm withBorder={false} sx={{ mt: -3.5}}>
        {header?.updated_at_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Actualizado: {header.updated_at_date}
          </Typography>
        )}
        {reportType === GERENCIALES_REPORT_TYPE && header?.total_contracts != null && (
          <Typography variant="body2" color="text.secondary">
            Total contratos: {header.total_contracts}
          </Typography>
        )}
        {reportType !== GERENCIALES_REPORT_TYPE && header?.date_filter_text && (
          <Typography variant="body2" color="text.secondary">
            {header.date_filter_text}
          </Typography>
        )}
        {reportType === GERENCIALES_DEBT_BALANCE_REPORT_TYPE &&
          header?.total_contracts != null && (
            <Typography variant="body2" color="text.secondary">
              Total contratos: {header.total_contracts}
            </Typography>
          )}
        {reportType === GERENCIALES_DEBT_BALANCE_REPORT_TYPE &&
          header?.average_debt != null && (
            <Typography variant="body2" color="text.secondary">
              Mora promedio general: {formatCurrency(header.average_debt)}
            </Typography>
          )}
        {reportType !== GERENCIALES_REPORT_TYPE && header?.total_payments != null && (
          <Typography variant="body2" color="text.secondary">
            Total pagos: {header.total_payments}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </VerticalForm>
      <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
      <CustomButton
        variant="base"
        onClick={onDownloadPdf}
        disabled={loading || disableDownload}
        isLoading={pdfCooldown}
        buttonIcon={PictureAsPdfIcon}
        sx={{ mt: 1.5 }}
      >
        Descargar PDF
      </CustomButton>
    </HorizontalForm>
  );
};

export default GerencialesFilters;
